import { useState } from 'react';
import { IonContent, IonIcon, IonModal } from '@ionic/react';
import { shareSocialOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/useAuthStore';
import { ShareQRModal } from '../ui/ShareQRModal';
import { logShareFailure } from '../../utils/shareLog';

interface ShareAppCardProps {
  /**
   * Optional callback invoked when an unauth user taps Sign In or Sign Up
   * inside the share-prompt sheet. The HomePage (or any other host)
   * passes this in to open its own SignInModal — keeping modal ownership
   * with the page that mounted it. The `mode` parameter tells the host
   * which intent was tapped so it can set `SignInModal.initialMode` and
   * the modal header reads "Sign Up" (not "Sign In") when the user
   * clicked Sign Up. When omitted, the sheet still renders + the Share
   * button in it still works; only the sign-in links no-op.
   */
  onRequestSignIn?: (mode: 'signIn' | 'signUp') => void;
}

export const ShareAppCard: React.FC<ShareAppCardProps> = ({ onRequestSignIn }) => {
  const { t } = useTranslation('home');
  const { t: tCommon } = useTranslation('common');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [copied, setCopied] = useState(false);
  // Bottom-sheet state — only used when unauth. Same pattern as
  // `HomeFeedSortPill`: an IonModal with `breakpoints` + `initialBreakpoint`
  // + `handle` for a small bottom-sheet that the user can dismiss by
  // swiping down. Centered content, no positioning anchor required.
  const [sheetOpen, setSheetOpen] = useState(false);
  // Defer-share flag: when the user taps Share inside the sheet we want
  // the native share dialog to open AFTER the sheet has fully dismissed
  // (so it visually anchors to the in-card Share button instead of
  // floating where the sheet's Share button used to be). The flag is set
  // when the sheet's Share button is tapped; `onDidDismiss` fires the
  // actual `performShare` only when the flag is true, then clears it.
  const [shareAfterDismiss, setShareAfterDismiss] = useState(false);
  // QR popup (auth only). The reusable ShareQRModal owns the fetch + share; we
  // just toggle it open.
  const [qrOpen, setQrOpen] = useState(false);

  // Unauth share path only (auth users share from inside ShareQRModal): plain
  // app URL, no API call, no ref, no connection seeded — the share still works
  // but the recipient signs up cold without inviter attribution. The nudge
  // sheet that opens first steers them toward signing in before sharing.
  const performShare = async () => {
    try {
      const shareUrl = window.location.origin;

      if (navigator.share) {
        await navigator.share({ title: t('feed.share.shareTitle'), url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      // Silent on user-cancel (AbortError); logs real share/clipboard failures.
      logShareFailure(err, 'app-share-unauth');
    }
  };

  // Button click branches on auth: auth → open the QR popup; unauth → open the
  // nudge sheet and let the user choose Sign In, Sign Up, or proceed with the
  // plain-link share (no QR — that endpoint needs a session).
  const handleClick = () => {
    if (isAuthenticated) {
      setQrOpen(true);
    } else {
      setSheetOpen(true);
    }
  };

  const handleAuthLink = (mode: 'signIn' | 'signUp') => {
    setSheetOpen(false);
    onRequestSignIn?.(mode);
  };

  const handleSheetShare = () => {
    // Queue the share for `onDidDismiss`. Don't `await performShare()`
    // here — calling `navigator.share` while the IonModal is animating
    // out causes the native share dialog to anchor to the dismissing
    // sheet's button (or float once that button leaves the DOM). By
    // deferring until the sheet is fully gone, the share dialog opens
    // anchored to the in-card Share button which is back in the visible
    // layout by then.
    setShareAfterDismiss(true);
    setSheetOpen(false);
  };

  const handleSheetDismissed = () => {
    setSheetOpen(false);
    if (shareAfterDismiss) {
      setShareAfterDismiss(false);
      void performShare();
    }
  };

  return (
    // flex-wrap + grow ratio: the text keeps the lion's share of the row
    // (grow-[999]) so the button sits at its content width on the right when it
    // fits. Its `basis-[60%]` is the wrap trigger — once large text makes the
    // button wide enough that 60% + button > the card, the button drops to its
    // own line, where `grow` lets it fill the full width.
    <div className="rounded-2xl border border-border bg-card px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2.5">
      <div className="min-w-0 grow-[999] basis-[60%]">
        <p className="text-sm font-medium text-foreground">{t('feed.share.cardHeading')}</p>
        <p className="text-xs text-muted-foreground">{t('feed.share.cardBody')}</p>
      </div>
      <button
        type="button"
        onClick={handleClick}
        className="flex grow basis-auto shrink-0 items-center justify-center gap-1.5 rounded-full bg-[var(--ion-color-primary)] px-4 py-2 text-sm font-semibold text-white shadow-sm active:scale-[0.99] transition-transform disabled:opacity-60"
      >
        <IonIcon icon={shareSocialOutline} className="text-base" />
        {copied ? t('feed.share.copied') : t('feed.share.button')}
      </button>

      {/* Unauth nudge sheet. Bottom-sheet pattern matches `HomeFeedSortPill`.
          Contents (top → bottom, all centered):
            - primary Share button (proceeds with the plain-link share),
            - nudge paragraph,
            - Sign In / Sign Up text links (side by side, regular-size text). */}
      <IonModal
        isOpen={sheetOpen}
        onDidDismiss={handleSheetDismissed}
        breakpoints={[0, 0.35]}
        initialBreakpoint={0.35}
        handle
      >
        <IonContent>
          <div className="px-6 pb-8 pt-6 flex flex-col items-center text-center gap-5">
            <button
              type="button"
              onClick={handleSheetShare}
              className="flex items-center justify-center gap-1.5 rounded-full bg-[var(--ion-color-primary)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm active:scale-[0.99] transition-transform"
            >
              <IonIcon icon={shareSocialOutline} className="text-base" />
              {t('feed.share.button')}
            </button>
            <p className="text-sm text-muted-foreground max-w-xs">
              {t('feed.share.unauthNudge')}
            </p>
            <div className="flex items-center justify-center gap-6">
              <button
                type="button"
                onClick={() => handleAuthLink('signIn')}
                className="text-sm font-medium text-[var(--ion-color-primary)] underline underline-offset-2"
              >
                {tCommon('actions.signIn')}
              </button>
              <button
                type="button"
                onClick={() => handleAuthLink('signUp')}
                className="text-sm font-medium text-[var(--ion-color-primary)] underline underline-offset-2"
              >
                {tCommon('actions.signUp')}
              </button>
            </div>
          </div>
        </IonContent>
      </IonModal>

      {/* Auth QR popup — reusable centered dialog (also used on other share
          surfaces). Owns its own fetch + share. */}
      <ShareQRModal isOpen={qrOpen} onClose={() => setQrOpen(false)} route="/" />
    </div>
  );
};
